/**
 * fieldValueRules — the value semantics the records grid's Sort and Filter
 * MUST agree on.
 *
 * PURE. No React, no Dexie, no storage. Three bindings live here and nowhere
 * else: what counts as absence of data, which kind a field key carries, and
 * how two strings order. A rep sees ONE grid — if Sort called a cell blank
 * and Filter did not, or if a pick list collated differently from the rows it
 * filters, the two surfaces would be answering different questions about the
 * same data. Single-definition is the guarantee that they cannot drift.
 */

import {
  EDITABLE_FIELDS, READONLY_DISPLAY_FIELDS, DISPOSAL_ONLY_FIELDS,
} from './editableFields';

/**
 * Absence of data — three tests, in this order. Note `false` and `0` are
 * values and are NOT empty; a FALSE flag is data and behaves as one.
 *
 * ⚠ DIVERGES FROM BOB, deliberately. His evalFilter answers 'is empty' with a
 * TRIMMED string test, so a whitespace-only value counts as empty for him.
 * Ours does not, because 'is empty' has to mean exactly what the grid's
 * empty-sorts-last rule means — otherwise a rep gets one answer from Sort and
 * a different one from Filter about the same blank cell.
 */
export function isEmpty(value) {
  return value === null || value === undefined || value === '';
}

/**
 * key → kind, from the same three manifests useFieldSortState validates
 * against. The four READONLY_DISPLAY_FIELDS entries carry NO kind at all, so
 * they land here as undefined and fall to the string arm — which is the
 * correct treatment for PRELIMINARY TOOL STATUS / TOOL ID and the two
 * server-computed rows. (PLATFORM was here until it was promoted to an
 * editable kind:'text' field; text and no-kind take the same arm, so the
 * promotion changed which contributor supplies the key and nothing else.
 * OEM was here too until OEM arc Phase 2 made it kind:'enum' — unlike
 * platform, that DOES change its arm: Filter offers the enum operators and a
 * pick list, and a saved OEM condition using 'contains' / 'does not contain'
 * no longer validates.)
 *
 * MEMBERSHIP DOUBLES AS THE LIVE-KEY TEST. `.has()` is true for a key that was
 * set even when its value is undefined, which is exactly those four no-kind
 * rows. So KIND_BY_KEY.has(k) is the same union SORTABLE_KEYS expresses, and
 * one lookup answers both "does this field still exist" and "which operator
 * set applies".
 */
export const KIND_BY_KEY = new Map(
  [...EDITABLE_FIELDS, ...READONLY_DISPLAY_FIELDS, ...DISPOSAL_ONLY_FIELDS]
    .map((f) => [f.key, f.kind]),
);

/**
 * ONE collator, shared. The grid's text comparisons and the filter's enum
 * option lists both order through it, so a pick list reads in the same order
 * as the rows it filters.
 *
 * numeric: true is the whole point — it segments digit runs and compares them
 * as numbers, so TOOL-2 sorts before TOOL-10. A plain codepoint compare puts
 * TOOL-10 first, on exactly the field a rep is most likely to reach for.
 *
 * 'en' INSTEAD OF undefined. `undefined` resolves to the device's own locale,
 * so two reps on two phones could see one site ordered two ways. Pinning the
 * locale fixes the collation to one table and makes the order a property of
 * the data rather than of the handset.
 *
 * sensitivity: 'base' INSTEAD OF .toLowerCase(). Case folding is what the
 * collator's own sensitivity level is for, and pre-folding then comparing is
 * not free: it mangles the input the numeric segmentation runs over. Fold via
 * the collator, never before it.
 *
 * HOISTED, not String.prototype.localeCompare. The two are semantically
 * identical given the same locale and options — this is a performance change
 * and nothing else. localeCompare resolves and builds a collator on EVERY
 * call: a 841-row site (the largest in TEST) sorted on two levels is on the
 * order of 16k comparisons, so that is 16k collators constructed and thrown
 * away inside one Apply tap, on a phone. Building once at module scope and
 * reusing is the documented reason Intl.Collator exists as a separate object
 * from the String method.
 */
export const TEXT_COLLATOR = new Intl.Collator('en', {
  numeric: true,
  sensitivity: 'base',
});
